import { useState, useRef } from "react";
import { FaStar, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTheme } from "../hooks/useTheme";
import { useLang } from "../hooks/useLang";

const GOLD = "#C9A84C";
const DARK = "#2A2E3A";

export type Testimonial = {
  name: string;
  role: string;
  text: string;
};

interface Props {
  testimonials: Testimonial[];
}

export default function TestimonialsCarousel({ testimonials }: Props) {
  const { theme } = useTheme();
  const { currentLang } = useLang();
  const isDark = theme === "dark";
  const isRTL = currentLang === "ar";

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const prev = () => {
    setActive((a) => (a === 0 ? testimonials.length - 1 : a - 1));
  };
  const next = () => {
    setActive((a) => (a === testimonials.length - 1 ? 0 : a + 1));
  };

  const cardBg = isDark ? DARK : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const textSecondary = isDark ? "#9ca3af" : "#6b7280";

  return (
    <div className="relative" dir={isRTL ? "rtl" : "ltr"}>
      {/* ── Infinite scroll band (desktop 3-visible) ─────────── */}
      <div
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          ref={trackRef}
          className="flex gap-4 sm:gap-6"
          style={{
            animation: paused ? "none" : "carouselScroll 28s linear infinite",
            width: "max-content",
          }}
        >
          {[...testimonials, ...testimonials].map((tm, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[85vw] xs:w-72 sm:w-80 p-5 sm:p-6 rounded-2xl border flex flex-col"
              style={{ background: cardBg, border: `1px solid ${cardBorder}`, maxWidth: "20rem" }}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <FaStar key={j} size={12} style={{ color: GOLD }} />
                ))}
              </div>
              <p className="text-sm leading-relaxed italic flex-1 mb-5" style={{ color: textSecondary }}>
                "{tm.text}"
              </p>
              <div className="flex items-center gap-3 mt-auto">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                  style={{ background: `${GOLD}20`, color: GOLD, border: `2px solid ${GOLD}50` }}
                >
                  {tm.name[0]}
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: isDark ? "#fff" : "#1a1d26" }}>{tm.name}</div>
                  <div className="text-xs" style={{ color: textSecondary }}>{tm.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Prev / Next ───────────────────────────────────────── */}
      <div className={`flex items-center justify-center gap-4 mt-8`}>
        <button
          onClick={prev}
          className="w-11 h-11 rounded-full flex items-center justify-center border transition-all hover:scale-110"
          style={{
            borderColor: `${GOLD}50`,
            background: `${GOLD}12`,
            color: GOLD,
          }}
          aria-label="Témoignage précédent"
        >
          {isRTL ? <FaChevronRight size={13} /> : <FaChevronLeft size={13} />}
        </button>

        {/* Dots */}
        <div className="flex gap-2" role="tablist" aria-label="Navigation des témoignages">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="rounded-full transition-all duration-300 relative"
              style={{
                width: i === active ? 24 : 24,
                height: 24,
                background: "transparent",
                padding: 0,
              }}
              role="tab"
              aria-selected={i === active}
              aria-label={`Aller au témoignage ${i + 1}`}
            >
              <span
                aria-hidden="true"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-300 pointer-events-none"
                style={{
                  width: i === active ? 24 : 8,
                  height: 8,
                  background: i === active ? GOLD : `${GOLD}30`,
                }}
              />
            </button>
          ))}
        </div>

        <button
          onClick={next}
          className="w-11 h-11 rounded-full flex items-center justify-center border transition-all hover:scale-110"
          style={{
            borderColor: `${GOLD}50`,
            background: `${GOLD}12`,
            color: GOLD,
          }}
          aria-label="Témoignage suivant"
        >
          {isRTL ? <FaChevronLeft size={13} /> : <FaChevronRight size={13} />}
        </button>
      </div>
    </div>
  );
}
